/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Text,
  Button,
  Section,
  Box,
  Link,
  HoursAndAddress,
  ArrowLink,
  Grid,
  Flex,
  IntrinsicImage,
  IntrinsicBox,
  SanityRichText,
  useThemeUI,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import invariant from 'invariant';
import { H } from '@hzdg/sectioning';
import React, { useState } from 'react';

import { getHoursProps, SanityHourDataProps } from './utils';

import type { Block } from '@sanity/block-content-to-react';

const categorySlug = (category: string) => {
  if (category === 'shop') {
    return 'shops';
  }
  if (category === 'amenity') {
    return 'amenities';
  }
  if (category === 'dine') {
    return 'dine';
  }

  return null;
};

const CategoryIcon = (reservationUrl: string) => {
  if (reservationUrl.includes('opentable.com')) {
    return <Icons.OpentableIcon />;
  }
  if (reservationUrl.includes('resy.com')) {
    return <Icons.ResyLogo />;
  }

  return null;
};

const categoryLabel = (category: string) => {
  if (category === 'shop') {
    return 'Shops';
  }
  if (category === 'amenity') {
    return 'Amenities & Services';
  }
  if (category === 'dine') {
    return 'Dine';
  }

  return null;
};

const BusinessDetailHeroBlock = ({
  data: { business },
}: {
  data: Queries.BusinessDetailQuery;
}): JSX.Element | null => {
  const [isVisible, toggleVisible] = useState<boolean>(false);
  const themeUI = useThemeUI();

  invariant(business, 'Business data is required!');

  const category = business.category?.category ?? '';
  const isUnder30 = !!business.under30Offer;

  if (!categorySlug || !categoryLabel) {
    console.warn(`No category slug or label for ${category}`);
    return null;
  }

  return (
    <Section
      data-section="BusinessDetailHero"
      p={[3, 5, 6]}
      sx={{ maxWidth: '1265px', margin: 'auto' }}
    >
      <Grid columns={[1, null, 2]} gap={[2, 8]}>
        <Box sx={{ margin: 'auto 0' }}>
          <ArrowLink
            href={`/${categorySlug(category)}`}
            label={`Back to ${categoryLabel(category)}`}
            reverse
            sx={{ justifyContent: 'flex-end' }}
          />
          <Box>
            <H
              sx={{
                fontWeight: 500,
                fontSize: '18px',
                textTransform: 'capitalize',
                mt: 4,
              }}
            >
              {categoryLabel(category)}
            </H>
            <H
              sx={{
                variant: 'styles.h1',
                my: 3,
                fontFamily: 'headingSecondary',
              }}
            >
              {business.titleAndSlug?.title}
            </H>
          </Box>

          <Grid columns={[1, 2]} gap={4} sx={{ py: 2 }}>
            <HoursAndAddress
              addressHeading={
                business?.contactsInfo?.[0]
                  ? 'Contact & Directions'
                  : 'Directions'
              }
              contactsInfo={business.contactsInfo?.map((info) => {
                return {
                  type: info?.type ?? '',
                  value: info?.value ?? '',
                };
              })}
              location={{
                title: business.location?.title ?? '',
                address1: business.location?.address1 ?? '',
                address2: business.location?.address2 ?? '',
              }}
            />
            {business.hours?.[0] && (
              <HoursAndAddress
                hours={getHoursProps(business as SanityHourDataProps)}
                hoursHeading="Hours"
              />
            )}
          </Grid>
          {isUnder30 && (
            <Grid
              columns={['1fr 6fr']}
              gap={0}
              sx={{
                mb: 4,
                display: ['grid', 'none'],
                position: 'relative',
              }}
            >
              <Icons.Under30RockLockup
                aria-label="Under 30"
                style={{
                  color: 'text',
                  fill: 'text',
                  position: 'relative',
                  marginRight: '20px',
                  width: 100,
                  height: 100,
                }}
              />
              <Text
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {business.under30Offer}
              </Text>
            </Grid>
          )}
          <Box>
            <Button
              onClick={() => toggleVisible(!isVisible)}
              sx={{
                mb: [3, null],
                display: ['block', 'none'],
              }}
              variant="text"
            >
              {isVisible ? 'Hide Full Details -' : 'See Full Details +'}
            </Button>
            <Link
              href={`https://www.google.com/maps/search/?api=1&query= ${business.titleAndSlug?.title} ${business.location?.address1}`}
              sx={{
                mb: 4,
                display: [isVisible ? 'inline-block' : 'none', 'inline-block'],
              }}
              variant="underline"
            >
              VIEW ON MAP
            </Link>
            <Box
              sx={{
                width: ['100%', 'unset'],
                mb: [4, null, null, 0],
                display: [isVisible ? 'block' : 'none', 'block'],
              }}
            >
              {business.orderNow?.url && (
                <Link
                  href={business.orderNow.url}
                  sx={{
                    mt: 2,
                    mb: 4,
                    display: 'block',
                  }}
                >
                  <Button px={4} py={3} variant="inverted">
                    <Flex>
                      <Box mr={2}>{business.orderNow.caption}</Box>
                      <Icons.SidewaysArrow
                        aria-hidden
                        sx={{ alignSelf: 'center' }}
                      />
                    </Flex>
                  </Button>
                </Link>
              )}
            </Box>
            <Box
              sx={{
                width: ['100%', 'unset'],
                mb: [4, null, null, 0],
                display: [isVisible ? 'block' : 'none', 'block'],
              }}
            >
              {business.reservation?.url && (
                <Link
                  href={business.reservation.url}
                  sx={{
                    mt: 2,
                    mb: 4,
                    display: 'block',
                  }}
                >
                  <Button px={4} py={3} variant="inverted">
                    <Flex sx={{ alignItems: 'center' }}>
                      <Box mr={2}>{business.reservation.type}</Box>
                      {CategoryIcon(business.reservation.url)}
                    </Flex>
                  </Button>
                </Link>
              )}
            </Box>
            <Text
              sx={{
                variant: 'smallP',
                mb: 4,
                display: [isVisible ? 'block' : 'none', 'block'],
              }}
            >
              {business._rawBody && (
                <SanityRichText
                  blocks={business._rawBody as unknown as Block[]}
                />
              )}
            </Text>
            {isUnder30 && (
              <Box
                sx={{
                  border: '1px solid',
                  padding: 1,
                  display: ['none', 'block'],
                  mb: 4,
                }}
              >
                <Box sx={{ border: '1px solid', padding: 20 }}>
                  <Grid
                    columns={['1fr 4fr']}
                    gap={0}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    <Icons.Under30RockLockup
                      aria-label="Under 30"
                      style={{
                        color: themeUI.theme.colors.text,
                        fill: 'text',
                        position: 'relative',
                        width: 85,
                        height: 85,
                        marginRight: '20px',
                      }}
                    />
                    <Text
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      {business.under30Offer}
                    </Text>
                  </Grid>
                </Box>
              </Box>
            )}
            {business.website?.url && business.website.caption && (
              <Link
                href={business.website.url}
                sx={{
                  mb: 4,
                  display: [
                    isVisible ? 'inline-block' : 'none',
                    'inline-block',
                  ],
                }}
                variant="underline"
              >
                <Flex>
                  <Text mr={2}>{business.website.caption}</Text>
                  <Icons.SidewaysArrow
                    aria-hidden
                    sx={{ alignSelf: 'center' }}
                  />
                </Flex>
              </Link>
            )}
          </Box>
        </Box>
        <IntrinsicBox>
          {business?.poster?.asset?.gatsbyImageData && (
            <IntrinsicImage
              image={business?.poster?.asset?.gatsbyImageData}
              ratio={[16 / 20, null, 13 / 20]}
            />
          )}
        </IntrinsicBox>
      </Grid>
    </Section>
  );
};

export default BusinessDetailHeroBlock;
